import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { canAccess } from "@/lib/permissions";
import {
  LayoutDashboard,
  Truck,
  MapPin,
  ClipboardList,
  Users,
  LogOut,
  Activity,
  FolderKanban,
  Building2,
  ShieldCheck,
  UserCog,
  Database,
  Receipt,
  Layers,
  AlertOctagon,
  ClipboardCheck,
  BarChart3,
  Settings,
  ChevronRight,
  Package,
  Boxes,
  HeartPulse,
  Sparkles,
  Bell,
  Menu,
} from "lucide-react";
import ClientBrand from "@/components/app/ClientBrand";
import PoweredBySmScope from "@/components/app/PoweredBySmScope";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { brand } from "@/lib/branding";

const MAIN_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/loads", label: "Loads", icon: ClipboardList, testid: "nav-loads" },
  { to: "/tracking", label: "Live Tracking", icon: Activity, testid: "nav-tracking" },
  { to: "/hauler-invoices", label: "Hauler Invoices", icon: Receipt, testid: "nav-hauler-invoices" },
  { to: "/hauling-summary", label: "Hauling Summary", icon: BarChart3, testid: "nav-hauling-summary" },
];

// Collapsible groups. Each group is auto-expanded when one of its children
// is the active route. State is persisted per-group in localStorage so users'
// preferences carry across page refreshes.
const NAV_GROUPS = [
  {
    key: "fleet",
    label: "Fleet & Personnel",
    icon: Truck,
    testid: "nav-group-fleet",
    storageKey: "sidebar.fleet.open",
    adminOnly: false,
    items: [
      { to: "/haulers", label: "Haulers", icon: Building2, testid: "nav-haulers" },
      { to: "/fleet", label: "Fleet", icon: Truck, testid: "nav-fleet" },
      { to: "/drivers", label: "Drivers", icon: Users, testid: "nav-drivers" },
    ],
  },
  {
    key: "locations",
    label: "Locations & Materials",
    icon: MapPin,
    testid: "nav-group-locations",
    storageKey: "sidebar.locations.open",
    adminOnly: false,
    items: [
      { to: "/projects", label: "Projects", icon: FolderKanban, testid: "nav-projects" },
      { to: "/sites", label: "Sites", icon: MapPin, testid: "nav-sites" },
      { to: "/stockpiles", label: "Stockpiles", icon: Layers, testid: "nav-stockpiles" },
    ],
  },
  {
    key: "quality",
    label: "Quality & Compliance",
    icon: ShieldCheck,
    testid: "nav-group-quality",
    storageKey: "sidebar.quality.open",
    adminOnly: false,
    items: [
      { to: "/compliance", label: "Compliance", icon: Sparkles, testid: "nav-compliance" },
      { to: "/exceptions", label: "Exceptions", icon: AlertOctagon, testid: "nav-exceptions" },
      { to: "/verification", label: "Verification", icon: ClipboardCheck, testid: "nav-verification" },
    ],
  },
  {
    key: "admin",
    label: "System Administration",
    icon: Settings,
    testid: "nav-system-admin",
    storageKey: "sidebar.admin.open",
    adminOnly: true,
    items: [
      { to: "/users", label: "Users", icon: UserCog, testid: "nav-users" },
      { to: "/audit", label: "Audit Log", icon: ShieldCheck, testid: "nav-audit" },
      { to: "/export", label: "Data Export", icon: Database, testid: "nav-export" },
      { to: "/system/container-types", label: "Container Types", icon: Package, testid: "nav-container-types" },
      { to: "/system/material-types", label: "Material Types", icon: Boxes, testid: "nav-material-types" },
      { to: "/system/health", label: "System Health", icon: HeartPulse, testid: "nav-system-health" },
    ],
  },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Poll unread notification count every 60s so the bell badge stays fresh.
  // Also refetches immediately when the user navigates back to the Notifications
  // page so a mark-all-read clears the badge without a full page reload.
  const [unreadCount, setUnreadCount] = useState(0);
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/notifications/unread-count");
        if (!cancelled) setUnreadCount(data.count || 0);
      } catch {
        // silent — auth interceptor handles 401s
      }
    };
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user, location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const visibleMain = MAIN_NAV.filter((item) => canAccess(user?.role, item.to));

  // Mobile drawer open state — auto-closes when the route changes so tapping
  // a nav item feels responsive instead of leaving the drawer overlay behind.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  const desktopSidebar = (
    <SidebarInner
      user={user}
      pathname={location.pathname}
      visibleMain={visibleMain}
      unreadCount={unreadCount}
      handleLogout={handleLogout}
    />
  );
  const mobileSidebar = (
    <SidebarInner
      user={user}
      pathname={location.pathname}
      visibleMain={visibleMain}
      unreadCount={unreadCount}
      handleLogout={handleLogout}
      mobile
    />
  );

  return (
    <div className="min-h-screen flex bg-white">
      {/* Desktop sidebar — hidden below lg (1024px) so phones in landscape
          and iPads in portrait get the mobile drawer instead of a squished
          fixed sidebar. */}
      <aside
        className="hidden lg:flex w-60 border-r border-gray-200 bg-white flex-col sticky top-0 h-screen self-start"
        data-testid="sidebar"
      >
        {desktopSidebar}
      </aside>

      {/* Mobile top bar — shown below lg (phones + iPads in portrait).
          Hamburger opens a Sheet drawer that hosts the same sidebar contents.
          Uses env(safe-area-inset-top) so the button sits below the iOS notch/
          dynamic island rather than under it. */}
      <header
        className="lg:hidden fixed inset-x-0 top-0 z-30 bg-white border-b border-gray-200 flex items-center justify-between px-3 pt-[env(safe-area-inset-top)]"
        style={{ minHeight: "calc(3.5rem + env(safe-area-inset-top))" }}
        data-testid="mobile-topbar"
      >
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetTrigger
            data-testid="mobile-nav-toggle"
            aria-label="Open navigation menu"
            className="w-12 h-12 flex items-center justify-center border border-gray-300 hover:bg-gray-50 active:bg-gray-100 transition-colors"
          >
            <Menu className="w-6 h-6" strokeWidth={2.25} />
          </SheetTrigger>
          <SheetContent
            side="left"
            className="w-72 landscape:w-80 p-0 flex flex-col max-h-[100dvh] h-[100dvh]"
            data-testid="mobile-nav-drawer"
          >
            {mobileSidebar}
          </SheetContent>
        </Sheet>

        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-gray-900 flex items-center justify-center">
            <Truck className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
          </div>
          <div className="font-display font-black text-base leading-none tracking-tight">
            SOILHAUL
          </div>
        </div>

        {/* Quick-access bell on the mobile top bar so users don't have to
            open the drawer just to check the inbox. */}
        <NavLink
          to="/notifications"
          data-testid="mobile-nav-notifications"
          aria-label="Notifications"
          className="relative w-12 h-12 flex items-center justify-center border border-gray-300 hover:bg-gray-50 active:bg-gray-100 transition-colors"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </NavLink>
      </header>

      {/* Content — extra top padding on mobile/tablet-portrait to clear the fixed top bar (including safe-area). */}
      <main className="flex-1 min-w-0 bg-gray-50 pt-[calc(3.5rem+env(safe-area-inset-top))] lg:pt-0 flex flex-col">
        <div className="flex-1"><Outlet /></div>
        <PoweredBySmScope />
      </main>
    </div>
  );
}

function NavItem({ item, nested = false, mobile = false }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={item.testid}
      className={({ isActive }) =>
        `flex items-center gap-3 py-2.5 ${mobile ? "landscape:py-1.5" : ""} text-sm transition-colors border-l-2 ${
          nested ? "pl-12 pr-6" : "px-6"
        } ${
          isActive
            ? "border-orange-500 bg-gray-50 text-gray-900 font-semibold"
            : "border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900"
        }`
      }
    >
      <Icon className="w-4 h-4" strokeWidth={2} />
      {item.label}
    </NavLink>
  );
}

/**
 * The inner sidebar content — brand block + nav list + user footer.
 * Used inside both the desktop fixed sidebar AND the mobile drawer sheet
 * so the two experiences render identically without duplicated markup.
 */
function SidebarInner({ user, pathname, visibleMain, unreadCount, handleLogout, mobile = false }) {
  return (
    <>
      <div className={`${mobile ? "flex-shrink-0 px-5 py-4 landscape:px-4 landscape:py-2.5" : "px-6 py-6"} border-b border-gray-200`}>
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 ${mobile ? "landscape:w-7 landscape:h-7" : ""} bg-gray-900 flex items-center justify-center`}>
            <Truck className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className={`font-display font-black text-lg ${mobile ? "landscape:text-base" : ""} leading-none tracking-tight`}>SOILHAUL</div>
            <div className={`text-[9px] tracking-[0.25em] uppercase text-gray-500 mt-0.5 ${mobile ? "landscape:hidden" : ""}`}>{brand.tagline}</div>
          </div>
        </div>
        <ClientBrand variant="compact" className={`${mobile ? "mt-3 pt-3 landscape:mt-2 landscape:pt-2" : "mt-4 pt-4"} border-t border-gray-100`} />
      </div>

      <nav className={mobile ? "flex-1 min-h-0 overflow-y-auto overscroll-contain py-2 landscape:py-1" : "flex-1 py-4 overflow-y-auto"}>
        {visibleMain.map((item) => (
          <NavItem key={item.to} item={item} mobile={mobile} />
        ))}

        {NAV_GROUPS.map((group) => {
          if (group.adminOnly && user?.role !== "admin") return null;
          const visibleItems = group.items.filter((it) => canAccess(user?.role, it.to));
          if (visibleItems.length === 0) return null;
          return (
            <NavGroup
              key={group.key}
              group={group}
              items={visibleItems}
              pathname={pathname}
              mobile={mobile}
            />
          );
        })}
      </nav>

      <div className={`border-t border-gray-200 ${mobile ? "flex-shrink-0 p-3 landscape:p-2" : "p-4"}`}>
        <div className={`mb-3 ${mobile ? "landscape:mb-2" : ""}`}>
          <div className="label-caps mb-1">Signed in</div>
          <div className="text-sm font-medium text-gray-900 truncate" data-testid="current-user-email">
            {user?.email}
          </div>
        </div>
        {/* Notifications is already on the mobile top bar, so we hide the
            duplicate here to save vertical space in the mobile drawer. */}
        {!mobile && (
          <NavLink
            to="/notifications"
            data-testid="nav-notifications"
            className={({ isActive }) =>
              `w-full relative flex items-center justify-center gap-2 px-3 py-2 mb-2 text-xs uppercase tracking-widest font-semibold border transition-colors ${
                isActive
                  ? "border-orange-500 bg-orange-500 text-white"
                  : "border-gray-300 hover:bg-gray-50"
              }`
            }
          >
            <Bell className="w-3.5 h-3.5" /> Notifications
            {unreadCount > 0 && (
              <span
                data-testid="notifications-unread-badge"
                className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white"
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </NavLink>
        )}
        {/* In landscape on phones, put Settings + Sign Out side-by-side to
            save vertical space. Portrait keeps them stacked. */}
        <div className={mobile ? "landscape:grid landscape:grid-cols-2 landscape:gap-2" : ""}>
          <NavLink
            to="/settings"
            data-testid="nav-settings"
            className={({ isActive }) =>
              `w-full flex items-center justify-center gap-2 px-3 py-2 ${mobile ? "landscape:py-1.5 mb-2 landscape:mb-0" : "mb-2"} text-xs uppercase tracking-widest font-semibold border transition-colors ${
                isActive
                  ? "border-orange-500 bg-orange-500 text-white"
                  : "border-gray-300 hover:bg-gray-50"
              }`
            }
          >
            <Settings className="w-3.5 h-3.5" /> Settings
          </NavLink>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className={`w-full flex items-center justify-center gap-2 px-3 py-2 ${mobile ? "landscape:py-1.5" : ""} text-xs uppercase tracking-widest font-semibold border border-gray-300 hover:bg-gray-900 hover:text-white transition-colors`}
          >
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>
      </div>
    </>
  );
}


/**
 * Collapsible nav section. Persists its open/closed state in localStorage,
 * auto-expands when the active route matches one of its children, and
 * highlights its header with the same orange accent as an active NavItem.
 */
function NavGroup({ group, items, pathname, mobile = false }) {
  const isChildActive = items.some((it) => pathname.startsWith(it.to) && it.to !== "/");
  const [open, setOpen] = useState(() => {
    const saved = typeof window !== "undefined"
      ? window.localStorage.getItem(group.storageKey)
      : null;
    if (saved !== null) return saved === "1";
    return isChildActive; // auto-open if landing on a child route
  });

  useEffect(() => {
    if (isChildActive && !open) setOpen(true);
    // Don't auto-collapse when navigating away — respect user's choice.
  }, [isChildActive]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(group.storageKey, open ? "1" : "0");
    }
  }, [open, group.storageKey]);

  const Icon = group.icon;
  return (
    <div className={mobile ? "mt-2 landscape:mt-1" : "mt-2"}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${group.testid}-toggle`}
        aria-expanded={open}
        className={`w-full flex items-center gap-3 px-6 py-2.5 ${mobile ? "landscape:py-1.5" : ""} text-sm transition-colors border-l-2 ${
          isChildActive
            ? "border-orange-500 bg-gray-50 text-gray-900 font-semibold"
            : "border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900"
        }`}
      >
        <Icon className="w-4 h-4" strokeWidth={2} />
        <span className="flex-1 text-left">{group.label}</span>
        <ChevronRight
          className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-90" : ""}`}
          strokeWidth={2}
        />
      </button>
      {open && (
        <div className="bg-gray-50/50" data-testid={`${group.testid}-children`}>
          {items.map((item) => (
            <NavItem key={item.to} item={item} nested mobile={mobile} />
          ))}
        </div>
      )}
    </div>
  );
}

